import { useCallback, useEffect, useState } from 'react';
import { alert, haptic } from '../../lib/telegram';
import { useT } from '../../lib/i18n';
import { api as regApi, NudgeSheet, runwayChip, type Runway } from '../../components/Regulars';

/**
 * «Постоянный», и одна строка о том, до какого числа он записан.
 *
 * The toggle is the whole feature's switch for this person: nothing is
 * warned about and nothing is ever sent unless the coach put the mark
 * here. The line under it appears only while the bookings are running
 * out, and carries the two things to do about it — the rest of the
 * screen stays what it was.
 */
export function RegularCard({ clientId, name, regular, onChanged }: {
  clientId: string; name: string; regular: boolean; onChanged: () => void;
}) {
  const t = useT();
  const [on, setOn] = useState(regular);
  const [state, setState] = useState<{ days: number; runway: Runway | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reminding, setReminding] = useState(false);
  // Короткое подтверждение после переключения: видно, что произошло.
  const [said, setSaid] = useState<string | null>(null);

  const load = useCallback(() => regApi<{ days: number; runway: Runway | null }>(
    `/api/coach/clients/${clientId}/runway`).then(setState).catch(() => setState(null)), [clientId]);
  useEffect(() => { if (on) void load(); else setState(null); }, [on, load]);

  async function toggle() {
    const next = !on;
    setOn(next);
    setBusy(true);
    try {
      await regApi(`/api/coach/clients/${clientId}/regular`, {
        method: 'PUT', body: JSON.stringify({ regular: next }),
      });
      haptic.success();
      // Не всплывающее окно: строка внизу, которая сама уходит. Тренер
      // ставит метку одним касанием и должен видеть результат, не
      // закрывая ничего.
      setSaid(next ? t('co.reg.onNow', { name }) : t('co.reg.offNow', { name }));
      setTimeout(() => setSaid(null), 2600);
      onChanged();
    } catch (err: any) {
      setOn(!next);
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  const r = state?.runway ?? null;
  const short = !!r && !!state && (r.days === null || r.days <= state.days);

  return (
    <>
      {/* Только когда запас кончается: в остальное время говорить не о чем. */}
      {on && short && r && (
        <div className="runline">
          <span className="runchip">{runwayChip(t, r)}</span>
          <span className="runline__acts">
            <button className="runline__act" onClick={() => { haptic.tap(); setReminding(true); }}>
              {t('co.reg.remind')}
            </button>
          </span>
        </div>
      )}
      <div className="regcard">
        <button type="button" className="regcard__row" role="switch" aria-checked={on}
          onClick={() => { if (!busy) void toggle(); }}>
          <span className="regcard__icon" aria-hidden="true">★</span>
          <span className="regcard__text">
            <b>{t('co.reg.rowLabel')}</b>
            <small>{t('co.reg.labelHint')}</small>
          </span>
          <span className="switch" aria-hidden="true"><span className="switch__knob" /></span>
        </button>
      </div>
      {said && <div className="regtoast">{said}</div>}
      {reminding && (
        <NudgeSheet client={{ clientId, name }}
          onClose={() => setReminding(false)}
          onSent={() => { setReminding(false); void load(); }} />
      )}
    </>
  );
}
